// Added by Sejune Oh on 2020-02-19
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { FC, useState, useEffect } from 'react'
import { createStyles, Theme, makeStyles, useTheme } from '@material-ui/core'
import classNames from 'classnames'

import {
    FormControl as MaterialFormControl,
    Select as MaterialSelect,
    MenuItem as MaterialMenuItem,
    FormHelperText as MaterialFormHelperText,
    Box as MaterialBox
} from '@/components/_primes/v2'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
export interface SelectProps {
    /** ID  */
    id: string
    /** Label  */
    label?: string
    /** 설명  */
    helperText?: string
    /** Full width box 여부  */
    fullwidth?: boolean
    /** Select에서 제공할 Options */
    menuItem: any[]
    /** Object 타입 Options에서 Value를 가지고 오는 함수 */
    valueGetter?: (item: any) => any
    /** Object 타입 Option에서 값이 되는 Prop */
    valueProp?: string
    /** Object 타입 Option에서 UI 상에 노출되는 값을 가지고 오는 함수  */
    dispNameGetter?: (item: any) => string
    /** Object 타입 Option에서 UI 상에 노출되는 Prop */
    dispNameProp?: string
    /** 이전에 선택한 값 / 기본 값 */
    defaultValue?: string | number
    /** Select 비활성화 여부 */
    disabled?: boolean
    /** Onchange Event handler */
    onChange?: (event: React.ChangeEvent<{
        name?: string | undefined;
        value: unknown;
    }>, child: React.ReactNode) => void
    /** OnFocus Event handler */
    onFocus?: (event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => void
    /** onBlur Event handler */
    onBlur?: (event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => any
    /** is Required */
    isRequired?: boolean
    /** Legacy Select Style */
    isLegacy?: boolean
}

/**
 * Material UI의 Select를 커스터마이징한 UI 입니다.
 */
const Select: FC<SelectProps> = ({
  id,
  label,
  helperText: argHelperText,
  onChange,
  menuItem,
  valueGetter,
  dispNameGetter,
  dispNameProp,
  valueProp,
  defaultValue: defaultValueArg,
  fullwidth = false,
  disabled = false,
  onFocus,
  onBlur,
  isRequired = false,
  isLegacy = false,
  ...props
}) => {
  const getValue = (item: any) => {
    if (valueGetter) return valueGetter(item)
    if (valueProp) return item[valueProp]
    return item
  }

  const getDispName = (item: any) => {
    if (dispNameGetter) return dispNameGetter(item)
    if (dispNameProp) return item[dispNameProp]
    return item
  }

  const [helperText, setHelperText] = useState<string | null>(argHelperText || null)
  const [focused, setFocused] = useState(false)
  const [menus, setMenus] = useState(menuItem)
  const [value, setValue] = useState<string | number | undefined>(defaultValueArg || getValue(menuItem[0]))
  const classes = useStyles()
  useTheme()

  // eslint-disable-next-line
  useEffect(() => setHelperText(argHelperText || null), [argHelperText])

  useEffect(() => {
    setMenus(menuItem)
    if (menuItem) setValue(defaultValueArg || getValue(menuItem[0]))

    // eslint-disable-next-line
  }, [menuItem, defaultValueArg])

  // # Focus evt handler
  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFocused(true)
    if (onFocus) onFocus(e)
  }

  // # Blur evt handler
  const handleBlur = async (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFocused(false)
    if (onBlur) onBlur(e)
  }

  const handleChange = (
    event: React.ChangeEvent<{ name?: string | undefined; value: unknown }>,
    child: React.ReactNode
  ) => {
    const {
      target: { value },
    } = event
    setValue(value as any)
    if (onChange) onChange(event, child)
  }

  return (
    <MaterialFormControl className={classes.formControl} variant="outlined">
      {label && (
        <MaterialBox
          component="span"
          className={classNames(classes.inputLabel, {
            'inputLabel-focused': focused,
            'inputLabel-disabled': disabled,
            'inputLabel-legacy': isLegacy,
          })}
          id={id + '-label'}
          dangerouslySetInnerHTML={{ __html: `${label} ${isRequired ? '*' : ''}` }}
        />
      )}
      <MaterialSelect
        id={id}
        labelId={id}
        inputProps={{ id }}
        value={value}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        disabled={disabled}
        className={classNames(classes.select, {
          'select-fullwidth': fullwidth,
        })}
        data-cy-select={id}
      >
        {menus.map((item, index) => (
          <MaterialMenuItem key={`${getValue(item)}-${index}`} value={getValue(item)} data-cy-value={getValue(item)}>
            {getDispName(item)}
          </MaterialMenuItem>
        ))}
      </MaterialSelect>
      {helperText && (
        <MaterialFormHelperText id="filled-weight-helper-text" className={classes.helperText} disabled={disabled}>
          {helperText}
        </MaterialFormHelperText>
      )}
    </MaterialFormControl>
  )
}

const useStyles = makeStyles((theme: Theme) => createStyles({
    inputLabel: {
        transition: 'color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
        color: '#fff',
        display: 'block',
        flex: 1,
        marginBottom: '6px',
        '&.inputLabel-focused': {
            color: '#145c82',
            fontWeight: 'bold'
        },
        '&.inputLabel-disabled': {
            color: 'rgba(255, 255, 255, 0.23)'
        },
        '&.inputLabel-legacy': {
            marginBottom: '6px',
            fontWeight: 'bold',
        }
    },
    select: {
        color: '#fff',
        flex: 1,
        width: '50%',
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#fff'
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#145c82'
        },
        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.23)',
            color: 'rgba(255, 255, 255, 0.23)'
        },
        '&.select-fullwidth': {
            width: '100%'
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.23)'
        },
        '& .MuiInputBase-input': {
            paddingTop: theme.spacing(),
            paddingBottom: theme.spacing(),
            paddingLeft: theme.spacing(),
        }
    },
    formControl: {
        position: 'relative',
        width: '100%',
        display: 'flex',
    },
    helperText: {
        flex: 1,
        color: 'rgba(255, 255, 255, 0.7)',
        margin: '8px 3px 0',
        '&.Mui-disabled': {
          color: 'rgba(255, 255, 255, 0.23)',
        },
    }
}))

export default Select
